import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  CalendarTodayOutlined as CalendarIcon,
  DeleteOutline as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterAltOffOutlined as ClearFiltersIcon,
  LocationOnOutlined as LocationIcon,
  PeopleAltOutlined as PeopleIcon,
  PlayArrowRounded as StartReviewIcon,
  RefreshRounded as ReopenIcon,
  SearchRounded as SearchIcon,
  WorkOutlineRounded as WorkIcon,
} from "@mui/icons-material";
import { Job, JobsState, ColumnId, JobDetail } from "../types/job";
import { deleteJob, fetchJobs, updateJobStatus } from "../Services/jobService";
import JobDetailSection from "./JobDetailSection";

type JobRow = Job & {
  columnId: ColumnId;
  location?: string;
  department?: string;
};

type SortValue = "created-desc" | "created-asc" | "deadline-asc";

interface ManageJobBoardProps {
  onCreateJob?: () => void;
  onViewCandidates?: (jobId: string, status: string) => void;
}

const STATUS_LABELS: Record<ColumnId, string> = {
  open: "Open",
  inReview: "In Review",
  closed: "Reviewed",
};

const COLORS = {
  teal: "#0F766E",
  tealDark: "#115E59",
  slate: "#475569",
  openBackground: "#EFF6FF",
  openBorder: "#BFDBFE",
  openText: "#1D4ED8",
  reviewBackground: "#FFF7ED",
  reviewBorder: "#FED7AA",
  reviewText: "#9A3412",
  reviewedBackground: "#F0FDF4",
  reviewedBorder: "#BBF7D0",
  reviewedText: "#15803D",
  neutralBackground: "#F8FAFC",
  neutralBorder: "#CBD5E1",
  danger: "#B42318",
  dangerBackground: "#FEF3F2",
  deadlineSoon: "#B45309",
};

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

const toTimestamp = (value?: string) => {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const formatDate = (value?: string) => {
  if (!value) return "Not set";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-GB", DATE_OPTIONS).format(date);
};

const primaryButtonSx = {
  minHeight: 42,
  textTransform: "none",
  fontWeight: 700,
  bgcolor: COLORS.teal,
  boxShadow: "none",
  "&:hover": { bgcolor: COLORS.tealDark, boxShadow: "none" },
};

const ManageJobBoard: React.FC<ManageJobBoardProps> = ({
  onCreateJob,
  onViewCandidates,
}) => {
  const [jobs, setJobs] = useState<JobsState>({
    open: [],
    inReview: [],
    closed: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | ColumnId>("all");
  const [sortBy, setSortBy] = useState<SortValue>("created-desc");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    onConfirm: async () => {},
  });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "warning" | "error" | "info",
  });

  const loadJobs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setJobs(await fetchJobs());
    } catch (loadError) {
      console.error("Error loading jobs:", loadError);
      setError(
        "We could not load the jobs. Check your connection and try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const allJobs = useMemo<JobRow[]>(
    () =>
      (Object.entries(jobs) as [ColumnId, Job[]][]).flatMap(
        ([columnId, items]) => items.map((job) => ({ ...job, columnId })),
      ),
    [jobs],
  );

  const visibleJobs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allJobs
      .filter((job) => {
        const matchesStatus =
          statusFilter === "all" || job.columnId === statusFilter;
        const matchesSearch =
          !term ||
          [job.title, job.location, job.department]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term));
        return matchesStatus && matchesSearch;
      })
      .sort((a, b) => {
        if (sortBy === "created-asc")
          return toTimestamp(a.createdOn) - toTimestamp(b.createdOn);
        if (sortBy === "deadline-asc") {
          return (
            (toTimestamp(a.deadline) || Number.MAX_SAFE_INTEGER) -
            (toTimestamp(b.deadline) || Number.MAX_SAFE_INTEGER)
          );
        }
        return toTimestamp(b.createdOn) - toTimestamp(a.createdOn);
      });
  }, [allJobs, search, statusFilter, sortBy]);

  const totalApplications = useMemo(
    () => allJobs.reduce((sum, job) => sum + (job.applicationsCount || 0), 0),
    [allJobs],
  );

  const showNotification = (
    message: string,
    severity: "success" | "warning" | "error" | "info" = "success",
  ) => setNotification({ open: true, message, severity });

  const moveJob = async (job: JobRow, toColumn: ColumnId) => {
    const previousJobs = jobs;
    const movedJob: Job = { ...job };
    delete (movedJob as Partial<JobRow>).columnId;

    if (toColumn === "open" && job.columnId === "closed") {
      movedJob.deadline = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];
      delete movedJob.reviewStarted;
      delete movedJob.closedDate;
      delete movedJob.finalizedCount;
      delete movedJob.shortlistedCount;
    }

    setJobs((current) => ({
      ...current,
      [job.columnId]: current[job.columnId].filter(
        (item) => item.id !== job.id,
      ),
      [toColumn]: [movedJob, ...current[toColumn]],
    }));

    try {
      await updateJobStatus(job.id, toColumn);
      showNotification(
        toColumn === "open"
          ? `“${job.title}” has been reopened.`
          : `“${job.title}” is now in review. Candidate ranking has started.`,
      );
    } catch (updateError) {
      console.error("Failed to update job status:", updateError);
      setJobs(previousJobs);
      showNotification(
        "The job status could not be changed. Please try again.",
        "error",
      );
    }
  };

  const requestStartReview = (job: JobRow) => {
    if (!job.applicationsCount) {
      showNotification(
        "This job has no applications to review yet.",
        "warning",
      );
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Start candidate review?",
      message: `HireSmart will begin processing ${job.applicationsCount} application${job.applicationsCount === 1 ? "" : "s"} for “${job.title}”.`,
      confirmLabel: "Start review",
      onConfirm: () => moveJob(job, "inReview"),
    });
  };

  const requestReopen = (job: JobRow) => {
    setConfirmDialog({
      open: true,
      title: "Reopen this job?",
      message: `“${job.title}” will return to Open with a new deadline seven days from today.`,
      confirmLabel: "Reopen job",
      onConfirm: () => moveJob(job, "open"),
    });
  };

  const requestDelete = (job: JobRow) => {
    if (job.applicationsCount > 0) {
      showNotification("A job with applications cannot be deleted.", "warning");
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete this job?",
      message: `“${job.title}” will be permanently deleted.`,
      confirmLabel: "Delete job",
      onConfirm: async () => {
        try {
          await deleteJob(job.id);
          setJobs((current) => ({
            ...current,
            open: current.open.filter((item) => item.id !== job.id),
          }));
          setExpandedJobId((current) => (current === job.id ? null : current));
          showNotification(`“${job.title}” has been deleted.`);
        } catch (deleteError) {
          console.error("Failed to delete job:", deleteError);
          showNotification(
            "The job could not be deleted. Please try again.",
            "error",
          );
        }
      },
    });
  };

  const handleJobUpdate = useCallback((detail: JobDetail) => {
    setJobs((current) => {
      const updated = { ...current };
      (Object.keys(updated) as ColumnId[]).forEach((columnId) => {
        updated[columnId] = updated[columnId].map((job) =>
          job.id === detail.jobId
            ? { ...job, title: detail.jobTitle, deadline: detail.deadline }
            : job,
        );
      });
      return updated;
    });
    showNotification("Job details updated successfully.");
  }, []);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setSortBy("created-desc");
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 10, textAlign: "center" }} aria-live="polite">
        <WorkIcon sx={{ fontSize: 44, color: COLORS.teal, mb: 1.5 }} />
        <Typography variant="h6" fontWeight={700}>
          Loading jobs…
        </Typography>
        <LinearProgress
          sx={{ width: 260, maxWidth: "80%", mx: "auto", mt: 2 }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={<Button onClick={loadJobs}>Try again</Button>}
        sx={{ alignItems: "center" }}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={750} letterSpacing="-0.025em">
            Jobs
          </Typography>
          <Typography color="text.secondary">
            {allJobs.length} job{allJobs.length === 1 ? "" : "s"} ·{" "}
            {totalApplications} application{totalApplications === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={onCreateJob}
          sx={primaryButtonSx}
        >
          Create job
        </Button>
      </Stack>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, md: 2 }, mb: 2.5, borderRadius: 2 }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by job title or location"
            inputProps={{ "aria-label": "Search jobs" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: { md: 180 } }}>
            <Select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value as "all" | ColumnId)
              }
              inputProps={{ "aria-label": "Filter jobs by status" }}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="inReview">In Review</MenuItem>
              <MenuItem value="closed">Reviewed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { md: 190 } }}>
            <Select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as SortValue)}
              inputProps={{ "aria-label": "Sort jobs" }}
            >
              <MenuItem value="created-desc">Newest created first</MenuItem>
              <MenuItem value="created-asc">Oldest created first</MenuItem>
              <MenuItem value="deadline-asc">Nearest deadline first</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Clear search, filter and sorting">
            <span>
              <Button
                variant="outlined"
                startIcon={<ClearFiltersIcon />}
                onClick={clearFilters}
                disabled={
                  !search && statusFilter === "all" && sortBy === "created-desc"
                }
                sx={{
                  minHeight: 40,
                  whiteSpace: "nowrap",
                  textTransform: "none",
                }}
              >
                Clear
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1.5 }}
        aria-live="polite"
      >
        Showing {visibleJobs.length} of {allJobs.length} jobs
      </Typography>

      <Stack spacing={1.5}>
        {visibleJobs.map((job) => {
          const expanded = expandedJobId === job.id;
          const deadlineTimestamp = toTimestamp(job.deadline);
          const deadlinePassed = Boolean(
            deadlineTimestamp && deadlineTimestamp < Date.now(),
          );
          const deadlineSoon = Boolean(
            deadlineTimestamp &&
            !deadlinePassed &&
            deadlineTimestamp - Date.now() <= 3 * 24 * 60 * 60 * 1000,
          );
          const deadlineColor = deadlinePassed
            ? COLORS.danger
            : deadlineSoon
              ? COLORS.deadlineSoon
              : COLORS.slate;

          const statusStyle =
            job.columnId === "open"
              ? {
                  bgcolor: COLORS.openBackground,
                  borderColor: COLORS.openBorder,
                  color: COLORS.openText,
                }
              : job.columnId === "inReview"
                ? {
                    bgcolor: COLORS.reviewBackground,
                    borderColor: COLORS.reviewBorder,
                    color: COLORS.reviewText,
                  }
                : {
                    bgcolor: COLORS.reviewedBackground,
                    borderColor: COLORS.reviewedBorder,
                    color: COLORS.reviewedText,
                  };

          return (
            <Paper
              key={job.id}
              component="article"
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                borderColor: expanded ? COLORS.teal : "divider",
                transition: "border-color 150ms ease",
              }}
            >
              <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={2}
                  alignItems={{ lg: "center" }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography
                        variant="h6"
                        component="h2"
                        fontWeight={750}
                        sx={{ mr: 0.5 }}
                      >
                        {job.title || "Untitled job"}
                      </Typography>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[job.columnId]}
                        variant="outlined"
                        sx={{ fontWeight: 750, ...statusStyle }}
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={{ xs: 1.5, md: 2.5 }}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1.25 }}
                    >
                      <Stack direction="row" spacing={0.7} alignItems="center">
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {job.location || "Location not specified"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.7} alignItems="center">
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          Created {formatDate(job.createdOn)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.7} alignItems="center">
                        <CalendarIcon
                          fontSize="small"
                          sx={{ color: deadlineColor }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: deadlineColor,
                            fontWeight:
                              deadlinePassed || deadlineSoon ? 700 : 400,
                          }}
                        >
                          Deadline {formatDate(job.deadline)}
                          {deadlinePassed
                            ? " · Passed"
                            : deadlineSoon
                              ? " · Due soon"
                              : ""}
                        </Typography>
                      </Stack>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<PeopleIcon fontSize="small" />}
                        onClick={() =>
                          onViewCandidates?.(
                            job.id,
                            STATUS_LABELS[job.columnId],
                          )
                        }
                        aria-label={`View ${job.applicationsCount || 0} applications for ${job.title}`}
                        sx={{
                          minHeight: 32,
                          px: 1,
                          py: 0.25,
                          color: "#1D4ED8",
                          bgcolor: "#EFF6FF",
                          border: "1px solid #BFDBFE",
                          borderRadius: 1.5,
                          textTransform: "none",
                          fontWeight: 750,
                          "&:hover": {
                            bgcolor: "#DBEAFE",
                            borderColor: "#60A5FA",
                          },
                        }}
                      >
                        {job.applicationsCount || 0} application
                        {job.applicationsCount === 1 ? "" : "s"}
                      </Button>
                    </Stack>
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ flexShrink: 0 }}
                  >
                    {job.columnId === "open" && (
                      <Button
                        variant="outlined"
                        startIcon={<StartReviewIcon />}
                        onClick={() => requestStartReview(job)}
                        sx={{
                          minHeight: 42,
                          textTransform: "none",
                          color: COLORS.reviewText,
                          borderColor: COLORS.reviewBorder,
                          bgcolor: COLORS.reviewBackground,
                          "&:hover": {
                            borderColor: COLORS.reviewText,
                            bgcolor: "#FFEDD5",
                          },
                        }}
                      >
                        Start review
                      </Button>
                    )}
                    {job.columnId === "closed" && (
                      <Button
                        variant="outlined"
                        startIcon={<ReopenIcon />}
                        onClick={() => requestReopen(job)}
                        sx={{
                          minHeight: 42,
                          textTransform: "none",
                          color: COLORS.teal,
                          borderColor: COLORS.openBorder,
                          bgcolor: COLORS.openBackground,
                          "&:hover": {
                            borderColor: COLORS.teal,
                            bgcolor: "#D1FAE5",
                          },
                        }}
                      >
                        Reopen
                      </Button>
                    )}
                    <Button
                      variant="text"
                      endIcon={
                        expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                      }
                      aria-expanded={expanded}
                      aria-controls={`job-details-${job.id}`}
                      onClick={() => setExpandedJobId(expanded ? null : job.id)}
                      sx={{
                        minHeight: 42,
                        textTransform: "none",
                        color: COLORS.slate,
                      }}
                    >
                      {expanded ? "Hide details" : "View details"}
                    </Button>
                    {job.columnId === "open" && job.applicationsCount === 0 && (
                      <Tooltip title="Delete job">
                        <IconButton
                          aria-label={`Delete ${job.title}`}
                          onClick={() => requestDelete(job)}
                          sx={{
                            color: COLORS.danger,
                            bgcolor: COLORS.dangerBackground,
                            border: "1px solid #FECDCA",
                            "&:hover": { bgcolor: "#FEE4E2" },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Box>

              {expanded && (
                <Box id={`job-details-${job.id}`}>
                  <Divider />
                  <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "grey.50" }}>
                    <JobDetailSection job={job} onJobUpdate={handleJobUpdate} />
                    <Divider sx={{ my: 2.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        variant="outlined"
                        startIcon={<ExpandLessIcon />}
                        onClick={() => setExpandedJobId(null)}
                        sx={{
                          minHeight: 42,
                          minWidth: 180,
                          color: COLORS.slate,
                          borderColor: COLORS.neutralBorder,
                          bgcolor: "background.paper",
                          textTransform: "none",
                          fontWeight: 700,
                          "&:hover": {
                            borderColor: COLORS.slate,
                            bgcolor: COLORS.neutralBackground,
                          },
                        }}
                      >
                        Hide details
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>
          );
        })}
      </Stack>

      {visibleJobs.length === 0 && (
        <Paper
          variant="outlined"
          sx={{ py: 8, px: 2, textAlign: "center", borderRadius: 2 }}
        >
          <WorkIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="h6" fontWeight={700}>
            No jobs found
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Try clearing the filters or create a new job.
          </Typography>
          <Stack direction="row" justifyContent="center" spacing={1}>
            <Button variant="outlined" onClick={clearFilters}>
              Clear filters
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateJob}
              sx={primaryButtonSx}
            >
              Create job
            </Button>
          </Stack>
        </Paper>
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog((current) => ({ ...current, open: false }))
        }
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() =>
              setConfirmDialog((current) => ({ ...current, open: false }))
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setConfirmDialog((current) => ({ ...current, open: false }));
              await confirmDialog.onConfirm();
            }}
            sx={primaryButtonSx}
          >
            {confirmDialog.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4500}
        onClose={() =>
          setNotification((current) => ({ ...current, open: false }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={notification.severity}
          onClose={() =>
            setNotification((current) => ({ ...current, open: false }))
          }
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageJobBoard;